import React, { useMemo, useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { RemediationJob } from '../../providers/RemediationJobProvider';
import { RemediationPlanDraft } from '../../modules/VulnerabilityCenter/Utils/types';

const MAX_PLAN_NAME_LENGTH = 500; // RemediationPlan.PlanName is nvarchar(500)

type Priority = 'normal' | 'emergency';

interface RemediationReviewModalProps {
  job: RemediationJob;
  isOtherTenantInView: boolean;
  onCancel: () => void;
  onConfirm: (drafts: RemediationPlanDraft[], isEmergency: boolean) => void;
}

const PRIORITIES: { value: Priority; label: string; description: string }[] = [
  {
    value: 'normal',
    label: 'Normal',
    description: "Updates install during each device group's maintenance window.",
  },
  {
    value: 'emergency',
    label: 'Emergency',
    description: 'Updates skip the maintenance window, so they can install while people are using their devices.',
  },
];

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="rounded-lg bg-zinc-800/70 px-3 py-2">
    <div className="text-xl font-semibold">{value}</div>
    <div className="text-xs text-gray-400">{label}</div>
  </div>
);

// Pops up on whichever page the user is on once the plans are worked out. Nothing is created until
// they confirm, and the one priority they choose applies to every plan in this job.
const RemediationReviewModal: React.FC<RemediationReviewModalProps> = ({ job, isOtherTenantInView, onCancel, onConfirm }) => {
  const [drafts, setDrafts] = useState(job.drafts);
  const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set());
  const [priority, setPriority] = useState<Priority | null>(null);
  const [showNotIncluded, setShowNotIncluded] = useState(false);

  const includedDrafts = useMemo(() => drafts.filter((draft) => !excludedKeys.has(draft.key)), [drafts, excludedKeys]);
  const deviceCount = useMemo(() => new Set(includedDrafts.flatMap((draft) => draft.deviceIds)).size, [includedDrafts]);
  const notIncludedItems = job.checkItems.filter((item) => item.stage === 'skipped' || item.stage === 'failed');
  const coveredCount = job.checkItems.length - notIncludedItems.length;
  const hasBlankName = includedDrafts.some((draft) => !draft.planName.trim());
  const canCreate = includedDrafts.length > 0 && !hasBlankName && priority !== null;

  const toggleDraft = (key: string) => {
    setExcludedKeys((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  };

  const renameDraft = (key: string, planName: string) => {
    setDrafts((prev) => prev.map((draft) => (draft.key === key ? { ...draft, planName } : draft)));
  };

  const handleCreate = () => {
    const plans = includedDrafts.map((draft) => ({ ...draft, planName: draft.planName.trim() }));
    onConfirm(plans, priority === 'emergency');
  };

  const planWord = `plan${includedDrafts.length !== 1 ? 's' : ''}`;
  const createLabel = `Create ${includedDrafts.length} ${priority === 'emergency' ? 'emergency ' : ''}${planWord}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="remediation-review-title"
        className="bg-[#1c1c1c] text-white rounded-xl shadow-2xl w-[960px] max-w-full h-[620px] max-h-[calc(100vh-32px)] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-gray-700 px-6 py-4">
          <div className="flex-1 min-w-0">
            <h2 id="remediation-review-title" className="text-lg font-semibold">
              Review your remediation plans
            </h2>
            <p className="text-sm text-gray-400">We've finished checking your selection. Nothing is created until you confirm.</p>
          </div>
          <button onClick={onCancel} aria-label="Close" className="text-gray-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Laptop and up: two panes that scroll on their own. Smaller screens: one stacked scroll. */}
        <div className="flex-1 min-h-0 overflow-y-auto lg:overflow-hidden lg:grid lg:grid-cols-[340px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] [scrollbar-width:thin] [scrollbar-color:#4b5563_transparent]">
          <div className="px-6 py-4 space-y-5 lg:overflow-y-auto lg:border-r lg:border-gray-700 [scrollbar-width:thin] [scrollbar-color:#4b5563_transparent]">
            {isOtherTenantInView && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                You've switched tenant. These plans are for the tenant you started from, not the one you're viewing now.
              </div>
            )}

            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-3">
              <Stat label={`${includedDrafts.length === 1 ? 'Plan' : 'Plans'} to create`} value={includedDrafts.length} />
              <Stat label={`${deviceCount === 1 ? 'Device' : 'Devices'} to update`} value={deviceCount} />
              <Stat label="Vulnerabilities covered" value={coveredCount} />
              <Stat label="Not included" value={notIncludedItems.length} />
            </div>

            {/* One priority for the whole job */}
            <fieldset>
              <legend className="text-sm font-semibold mb-1">How urgent is this?</legend>
              <p className="text-xs text-gray-400 mb-2">This applies to every plan.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-3">
                {PRIORITIES.map((option) => (
                  <label
                    key={option.value}
                    className={`flex gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                      priority === option.value ? 'border-red-600 bg-red-600/10' : 'border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="radio"
                      name="remediation-priority"
                      value={option.value}
                      checked={priority === option.value}
                      onChange={() => setPriority(option.value)}
                      aria-label={option.label}
                      className="mt-0.5 accent-red-600"
                    />
                    <span>
                      <span className="block text-sm font-medium">{option.label}</span>
                      <span className="block text-xs text-gray-400 mt-1">{option.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="px-6 pb-4 pt-0 lg:pt-4 space-y-5 lg:overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#4b5563_transparent]">
            {/* The plans */}
            <div>
              <h3 className="text-sm font-semibold mb-1">Your plans</h3>
              <p className="text-xs text-gray-400 mb-2">
                Each update gets its own plan, on only the devices that need it. Rename a plan or untick any you don't want.
              </p>
              <div className="space-y-3">
                {drafts.map((draft) => {
                  const included = !excludedKeys.has(draft.key);
                  return (
                    <div
                      key={draft.key}
                      className={`border border-gray-700 rounded-lg p-3 transition-opacity ${included ? '' : 'opacity-50'}`}
                    >
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={included}
                          onChange={() => toggleDraft(draft.key)}
                          aria-label={`Include ${draft.softwareName}`}
                          className="h-4 w-4 accent-red-600"
                        />
                        <span className="text-sm font-semibold flex-1 min-w-0 truncate" title={draft.softwareName}>
                          {draft.softwareName}
                        </span>
                        <span className="text-xs text-gray-300 flex-shrink-0">
                          {draft.deviceIds.length} device{draft.deviceIds.length !== 1 ? 's' : ''}
                        </span>
                      </label>
                      <input
                        type="text"
                        value={draft.planName}
                        disabled={!included}
                        maxLength={MAX_PLAN_NAME_LENGTH}
                        onChange={(event) => renameDraft(draft.key, event.target.value)}
                        aria-label={`Plan name for ${draft.softwareName}`}
                        className="mt-2 w-full bg-zinc-900 border border-gray-700 rounded-md px-3 py-1.5 text-sm text-white focus:outline-none focus:border-red-600 disabled:cursor-not-allowed"
                      />
                      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400">
                        {draft.kbId ? (
                          <span className="text-emerald-400">{draft.kbId}</span>
                        ) : (
                          <>
                            <span>{draft.currentVersions.join(', ')}</span>
                            <span>to</span>
                            <span className="text-emerald-400">{draft.targetVersion}</span>
                          </>
                        )}
                        {draft.cveIds.length > 0 && <span className="text-gray-500">· {draft.cveIds.join(', ')}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* What was left out, and why */}
            {notIncludedItems.length > 0 && (
              <div>
                <button
                  onClick={() => setShowNotIncluded(!showNotIncluded)}
                  aria-expanded={showNotIncluded}
                  className="flex items-center gap-1 text-sm text-gray-300 hover:text-white transition-colors"
                >
                  <ChevronRight size={16} className={`transition-transform ${showNotIncluded ? 'rotate-90' : ''}`} />
                  Not included ({notIncludedItems.length})
                </button>
                {showNotIncluded && (
                  <ul className="mt-2 border border-gray-700 rounded-lg divide-y divide-gray-800">
                    {notIncludedItems.map((item) => (
                      <li key={item.key} className="flex items-center justify-between gap-3 px-3 py-2 text-xs">
                        <span className="min-w-0 truncate text-gray-200" title={item.title}>
                          {item.title}
                        </span>
                        <span className="flex-shrink-0 text-gray-400">{item.message}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-3 border-t border-gray-700 px-6 py-4">
          <p className="flex-1 min-w-[200px] text-xs text-gray-400">
            {priority ? 'Plans are approved automatically once created.' : 'Choose how urgent this is to continue.'}
          </p>
          <button
            onClick={onCancel}
            className="px-5 py-2 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-700 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!canCreate}
            className="px-5 py-2 bg-red-700 hover:bg-red-800 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed rounded-full font-medium transition-all"
          >
            {createLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemediationReviewModal;
